/**
 * tableExport — CSV / XLSX (client-side) + PDF (server-side) exports.
 * Rows are already what the user sees on screen (post-filter/sort/group).
 */
import * as XLSX from "xlsx";
import api from "@/lib/api";
import { toast } from "sonner";

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/["\n,]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function exportCSV({ columns, rows, filename = "export.csv", grandTotals = null }) {
  const visible = columns.filter((c) => c.visible !== false);
  const header = visible.map((c) => csvEscape(c.label || c.key));
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(visible.map((c) => csvEscape(formatForExport(r, c))).join(","));
  }
  if (grandTotals) {
    lines.push(visible.map((c, i) => i === 0 ? "TOTAL" : csvEscape(grandTotals[c.key] ?? "")).join(","));
  }
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, filename);
}

export function exportXLSX({ columns, rows, filename = "export.xlsx", sheetName = "Sheet1", grandTotals = null }) {
  const visible = columns.filter((c) => c.visible !== false);
  const header = visible.map((c) => c.label || c.key);
  const data = [header];
  for (const r of rows) data.push(visible.map((c) => formatForExport(r, c)));
  if (grandTotals) {
    data.push(visible.map((c, i) => i === 0 ? "TOTAL" : (grandTotals[c.key] ?? "")));
  }
  const ws = XLSX.utils.aoa_to_sheet(data);
  // Column widths
  ws["!cols"] = visible.map((c) => ({ wch: Math.min(50, Math.max(10, (c.width || 12) / 8)) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 30));
  XLSX.writeFile(wb, filename);
}

export async function exportPDF({ workspaceId, tableKey, title, columns, rows, footer = null, grandTotals = null }) {
  const visible = columns.filter((c) => c.visible !== false);
  const cols = visible.map((c) => ({ key: c.key, label: c.label || c.key, width: c.width || null }));
  const shapedRows = rows.map((r) => {
    const o = {};
    for (const c of visible) o[c.key] = formatForExport(r, c);
    return o;
  });
  try {
    const res = await api.post(
      `/workspaces/${workspaceId}/tables/${tableKey}/export-pdf`,
      { title, columns: cols, rows: shapedRows, footer, grand_totals: grandTotals || null },
      { responseType: "blob" },
    );
    const blob = new Blob([res.data], { type: "application/pdf" });
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    triggerDownload(blob, `${tableKey}-${stamp}.pdf`);
    toast.success("PDF ready");
  } catch (e) {
    toast.error("PDF export failed");
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

function formatForExport(row, col) {
  const raw = typeof col.accessor === "function" ? col.accessor(row) : row[col.key];
  if (raw == null) return "";
  if (typeof col.exportValue === "function") return col.exportValue(row);
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  return raw;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
}
